/**
 * FollowUpOpportunityCard.jsx
 *
 * Displays a deferred/active/closed commercial follow-up opportunity on the
 * Lead/Client workspace Overview tab.  Provides staff actions:
 *   Follow Up Now  — reactivates a deferred follow-up immediately
 *   Change Date    — reschedule the follow-up date
 *   Edit Note      — update the internal note
 *   No Further Action — close the opportunity
 */
import { useState } from "react";
import {
  Calendar, CalendarClock, CheckCircle2, Clock, Edit3,
  X, Zap, ChevronDown, Loader2, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const REASON_LABELS = {
  future_service_need:    "Future Service Need",
  waiting_for_tax_period: "Waiting for Tax/Filing Period",
  waiting_for_client:     "Waiting for Client",
  waiting_for_life_event: "Waiting for Life/Business Event",
  planning_opportunity:   "Planning Opportunity",
  other:                  "Other",
};

const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([v, l]) => ({ value: v, label: l }));

function StatusChip({ status, followUpAt }) {
  const now = new Date();
  const due = followUpAt ? new Date(followUpAt) : null;
  const isDue = status === "active" || (status === "deferred" && due && due <= now);

  if (status === "closed") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] rounded-full px-2.5 py-0.5 bg-slate-100 text-slate-500 ring-1 ring-slate-200">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        No Further Action
      </span>
    );
  }
  if (isDue) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] rounded-full px-2.5 py-0.5 bg-amber-50 text-amber-700 ring-1 ring-amber-200">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Follow-Up Due
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] rounded-full px-2.5 py-0.5 bg-blue-50 text-blue-700 ring-1 ring-blue-200">
      <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
      Follow Up Later
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

// ── Change Date dialog ────────────────────────────────────────────────────────
function ChangeDateDialog({ followUp, onClose, onSaved }) {
  const [date,   setDate]   = useState((followUp.follow_up_at || "").slice(0, 10));
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!date) { toast.error("Please select a date"); return; }
    setBusy(true);
    try {
      await api.patch(`/meeting-followups/${followUp.id}`, { follow_up_at: date });
      toast.success("Follow-up date updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update date");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="fu-change-date-dialog">
        <div className="flex items-start justify-between mb-4">
          <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Change Follow-Up Date</h3>
        <p className="text-sm text-slate-500 mb-4">Update when this opportunity should resurface in Live Operations.</p>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">New Follow-Up Date</label>
        <input
          type="date"
          value={date}
          min={new Date().toISOString().slice(0, 10)}
          onChange={e => setDate(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          data-testid="fu-change-date-input"
        />
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={busy || !date} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 inline-flex items-center gap-2" data-testid="fu-change-date-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Date
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Note dialog ───────────────────────────────────────────────────────────
function EditNoteDialog({ followUp, onClose, onSaved }) {
  const [note, setNote] = useState(followUp.note || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/meeting-followups/${followUp.id}`, { note });
      toast.success("Note updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="fu-edit-note-dialog">
        <div className="flex items-start justify-between mb-4">
          <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <Edit3 className="h-5 w-5 text-slate-500" />
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Edit Internal Note</h3>
        <p className="text-sm text-slate-500 mb-4">Staff-only context. Never visible to the client.</p>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={4}
          placeholder="Add internal context about this opportunity..."
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
          data-testid="fu-edit-note-input"
        />
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-medium disabled:opacity-50 inline-flex items-center gap-2" data-testid="fu-edit-note-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main card component ────────────────────────────────────────────────────────
export default function FollowUpOpportunityCard({ followUp, onReload }) {
  const [showChangeDate,  setShowChangeDate]  = useState(false);
  const [showEditNote,    setShowEditNote]    = useState(false);
  const [reactivateBusy,  setReactivateBusy]  = useState(false);
  const [closeBusy,       setCloseBusy]       = useState(false);
  const [showClose,       setShowClose]       = useState(false);

  if (!followUp) return null;

  const { id, status, follow_up_at, reason, reason_label, note, origin_type,
          deferred_at, deferred_by_name } = followUp;
  const isClosed = status === "closed";
  const now      = new Date();
  const dueDate  = follow_up_at ? new Date(follow_up_at) : null;
  const isDue    = status === "active" || (status === "deferred" && dueDate && dueDate <= now);

  const handleReactivate = async () => {
    setReactivateBusy(true);
    try {
      await api.post(`/meeting-followups/${id}/reactivate`);
      toast.success("Follow-up is now active — it will appear in Live Operations");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to activate follow-up");
    } finally {
      setReactivateBusy(false);
    }
  };

  const handleClose = async () => {
    setCloseBusy(true);
    try {
      await api.post(`/meeting-followups/${id}/close`);
      toast.success("Follow-up closed — No Further Action");
      setShowClose(false);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close follow-up");
    } finally {
      setCloseBusy(false);
    }
  };

  const originLabel = origin_type === "zoom_consultation" ? "Zoom Consultation"
    : origin_type === "manual_transcript" ? "Uploaded Transcript"
    : "Meeting";

  return (
    <div
      className={cn(
        "rounded-2xl border p-5 transition-all",
        isClosed
          ? "border-slate-200 bg-slate-50"
          : isDue
          ? "border-amber-200 bg-amber-50/40"
          : "border-blue-200 bg-blue-50/30"
      )}
      data-testid="follow-up-opportunity-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className={cn(
            "h-9 w-9 rounded-xl flex items-center justify-center shrink-0",
            isClosed ? "bg-slate-100" : isDue ? "bg-amber-100" : "bg-blue-100"
          )}>
            <CalendarClock className={cn("h-4.5 w-4.5", isClosed ? "text-slate-400" : isDue ? "text-amber-600" : "text-blue-600")} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 mb-0.5">
              Follow-Up Opportunity
            </div>
            <StatusChip status={status} followUpAt={follow_up_at} />
          </div>
        </div>
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm mb-3">
        {follow_up_at && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">Follow-Up Date</div>
            <div className={cn("font-medium mt-0.5", isDue && !isClosed ? "text-amber-700" : "text-slate-800")}>
              {formatDate(follow_up_at)}
            </div>
          </div>
        )}
        {(reason || reason_label) && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">Reason</div>
            <div className="font-medium text-slate-800 mt-0.5">
              {reason_label || REASON_LABELS[reason] || reason}
            </div>
          </div>
        )}
        {origin_type && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">Origin</div>
            <div className="font-medium text-slate-800 mt-0.5 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-slate-400" />
              {originLabel}
            </div>
          </div>
        )}
        {deferred_at && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">Deferred</div>
            <div className="font-medium text-slate-600 mt-0.5 text-xs">
              {formatDate(deferred_at)}{deferred_by_name ? ` · ${deferred_by_name}` : ""}
            </div>
          </div>
        )}
      </div>

      {/* Note */}
      {note && (
        <div className="rounded-lg bg-white/80 border border-slate-200 px-3 py-2.5 mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400 mb-1">Internal Note</div>
          <p className="text-sm text-slate-700 leading-relaxed">{note}</p>
        </div>
      )}

      {/* Actions — only when not closed */}
      {!isClosed && (
        <div className="flex flex-wrap gap-2 mt-1" data-testid="fu-actions">
          {/* Follow Up Now — only when still deferred (not yet due/active) */}
          {status === "deferred" && dueDate && dueDate > now && (
            <button
              onClick={handleReactivate}
              disabled={reactivateBusy}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
              data-testid="fu-follow-up-now-btn"
            >
              {reactivateBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              Follow Up Now
            </button>
          )}
          <button
            onClick={() => setShowChangeDate(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-3 py-1.5 transition-colors"
            data-testid="fu-change-date-btn"
          >
            <Calendar className="h-3.5 w-3.5" />
            Change Date
          </button>
          <button
            onClick={() => setShowEditNote(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors"
            data-testid="fu-edit-note-btn"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Edit Note
          </button>
          <button
            onClick={() => setShowClose(true)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg px-3 py-1.5 transition-colors ml-auto"
            data-testid="fu-no-further-action-btn"
          >
            <X className="h-3.5 w-3.5" />
            No Further Action
          </button>
        </div>
      )}

      {/* Dialogs */}
      {showChangeDate && (
        <ChangeDateDialog followUp={followUp} onClose={() => setShowChangeDate(false)} onSaved={onReload} />
      )}
      {showEditNote && (
        <EditNoteDialog followUp={followUp} onClose={() => setShowEditNote(false)} onSaved={onReload} />
      )}
      {showClose && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowClose(false)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="fu-close-dialog">
            <div className="flex items-start justify-between mb-4">
              <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-slate-400" />
              </div>
              <button onClick={() => setShowClose(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-2">No Further Action?</h3>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This closes the active follow-up opportunity. The consultation, transcript, analysis, and
              Consultation Summary all remain intact. Future genuine activity can create a new opportunity normally.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowClose(false)} disabled={closeBusy} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button onClick={handleClose} disabled={closeBusy} className="px-4 py-2 text-sm rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-medium disabled:opacity-50 inline-flex items-center gap-2" data-testid="fu-close-confirm-btn">
                {closeBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                No Further Action
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
